import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { debugLog } from '@/utils/debug';
import { Formula, FormulaPoint } from '@/types/formula';
import { Point } from '@/types/shapes';
import { evaluateFormula, createFunctionFromExpression, buildEvaluableExpression } from '@/utils/formulaUtils';
import { useIsMobile } from '@/hooks/use-mobile';

// Define any types needed for this component
interface FormulaGraphProps {
  formula: Formula;
  gridPosition: Point;
  pixelsPerUnit: number;
  showPositiveQuadrant?: boolean;
  onPointSelect?: (point: FormulaPoint & {
    mathX: number;
    mathY: number;
    formula: Formula;
    pointIndex?: number;
    allPoints?: FormulaPoint[];
    navigationStepSize?: number;
  } | null) => void;
  globalSelectedPoint?: (FormulaPoint & {
    mathX: number;
    mathY: number;
    formula: Formula;
    pointIndex?: number;
  }) | null;
  showPath?: boolean;
  hideSelectedPoint?: boolean;
  isEmbedded?: boolean;
}

const FormulaGraph: React.FC<FormulaGraphProps> = ({ 
  formula, 
  gridPosition, 
  pixelsPerUnit,
  showPositiveQuadrant = false,
  onPointSelect,
  globalSelectedPoint,
  hideSelectedPoint,
  isEmbedded = false
}) => {
  // Check if we're on a mobile device
  const isMobile = useIsMobile();
  
  // Track if we're currently dragging to optimize rendering
  const isDraggingRef = useRef(false);
  const lastEvaluationTimeRef = useRef(0);
  const lastGridPositionRef = useRef<Point | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [_selectedPoint, setSelectedPoint] = useState<{ index: number, point: FormulaPoint } | null>(null);
  // Add a ref to store the last valid points
  const lastValidPointsRef = useRef<FormulaPoint[]>([]);
  const lastDragEndTimeRef = useRef(0);
  
  // Force re-evaluation when grid position changes significantly
  const gridPositionKey = useMemo(() => {
    // Round to nearest 0.5 pixel for more responsive updates
    const roundedX = Math.round(gridPosition.x * 2) / 2;
    const roundedY = Math.round(gridPosition.y * 2) / 2;
    return `${roundedX},${roundedY}`;
  }, [gridPosition]);
  
  // Detect rapid changes in grid position (indicating dragging)
  useEffect(() => {
    const now = Date.now();
    
    // If this is the first update or it's been a while since the last update, it's not dragging
    if (!lastGridPositionRef.current || now - lastEvaluationTimeRef.current > 300) {
      isDraggingRef.current = false;
    } else {
      // If we're getting rapid updates, we're likely dragging
      isDraggingRef.current = true;
    }
    
    // Update the last evaluation time and grid position
    lastEvaluationTimeRef.current = now;
    lastGridPositionRef.current = gridPosition;
  }, [gridPosition]);

  // Handle drag end
  useEffect(() => {
    const handleDragEnd = () => {
      const now = Date.now();
      if (now - lastDragEndTimeRef.current < 100) {
        return; // Debounce drag end handling
      }
      lastDragEndTimeRef.current = now;
      
      // Force a re-evaluation with high quality after drag ends
      setIsUpdating(true);
      const points = evaluateFormula(formula, gridPosition, pixelsPerUnit, false);
      if (points.length > 0) {
        lastValidPointsRef.current = points;
      }
      setIsUpdating(false);
    };

    // Use window.onmouseup instead of subscribing to isGridDragging
    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        handleDragEnd();
      }
    };

    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [formula, gridPosition, pixelsPerUnit]);

  // Check if this is a tangent function
  const isTangent = formula.expression.includes('Math.tan(') || 
                    formula.expression === 'Math.tan(x)' || 
                    formula.expression.includes('tan(x)');

  // Check if this is a logarithmic function
  const isLogarithmic = formula.expression.includes('Math.log(') ||
                        formula.expression.includes('Math.log10(') ||
                        formula.expression.includes('Math.log2(') ||
                        formula.expression.includes('log(') ||
                        formula.expression.includes('ln(');

  // Calculate points for the formula
  const points = useMemo(() => {
    // Evaluate with full quality to avoid incomplete plots persisting
    return evaluateFormula(formula, gridPosition, pixelsPerUnit, false);
  }, [formula, pixelsPerUnit, gridPosition]);

  // Handle point evaluation and updates
  useEffect(() => {
    setIsUpdating(true);
    debugLog('Evaluating formula');
    
    // We've already calculated the points in the useMemo above
    if (points.length > 0) {
      lastValidPointsRef.current = points;
    }
    
    debugLog('Generated points:', String(points.length));
    setIsUpdating(false);
  }, [formula, gridPositionKey, pixelsPerUnit, points, gridPosition]);
  
  // Helper function to find the closest point to given coordinates
  const findClosestPointIndex = useCallback((x: number, y: number): number => {
    if (!points || points.length === 0) return -1;
    
    let closestIndex = -1;
    let minDistance = Number.MAX_VALUE;
    
    points.forEach((point, index) => {
      if (!point.isValid) return;
      
      const dx = point.x - x;
      const dy = point.y - y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });
    
    return closestIndex;
  }, [points]);
  
  // Update local selected point when global selected point changes
  useEffect(() => {
    debugLog('FormulaGraph current formula id:', formula.id);
    
    // If there's no global selected point or it's for a different formula, clear local selection
    if (!globalSelectedPoint || globalSelectedPoint.formula.id !== formula.id) {
      debugLog('FormulaGraph: Clearing local selection');
      setSelectedPoint(null);
      return;
    }

    // Find the point in our local points array
    if (points && points.length > 0) {
      const pointIndex = findClosestPointIndex(globalSelectedPoint.x, globalSelectedPoint.y);
      
      if (pointIndex >= 0) {
        setSelectedPoint({
          index: pointIndex,
          point: points[pointIndex]
        });
      }
    } else {
      debugLog('FormulaGraph: No points available');
    }
  }, [globalSelectedPoint, formula.id, points, findClosestPointIndex]);
  
  // Log when points are updated
  useEffect(() => {
    if (points.length > 0) {
      debugLog('Formula points updated');
    }
    
    // Clear selected point when points change
    setSelectedPoint(null);
  }, [points, formula.id]);

  // Helper function to build a path string for the graph
  const createPath = (points: FormulaPoint[]): string => {
    if (points.length === 0) return '';

    let path = '';
    let penDown = false;
    
    // Get approximate canvas dimensions
    const canvasWidth = window.innerWidth;
    const canvasHeight = window.innerHeight;
    
    // Function to check if a point is within the visible canvas area with some margin
    const isWithinCanvas = (x: number, y: number): boolean => {
      // Determine appropriate margins based on function type
      let xMargin = 1000;
      let yMargin = 1000;
      
      // For logarithmic functions, use larger margins
      if (isLogarithmic) {
        xMargin = 20000;
        yMargin = 50000;
      }
      
      // For tangent functions, use much larger margins
      // Tangent functions have vertical asymptotes
      if (isTangent || 
          formula.expression.includes('Math.tan(x)') || 
          formula.expression.includes('tan(x)')) {
        xMargin = 10000;
        yMargin = 1000000; // Very large y-margin for tangent asymptotes
      }
      
      // For exponential functions, use much larger margins
      // Exponential functions grow extremely rapidly
      if (formula.expression.includes('Math.exp(x)') || 
          formula.expression.includes('exp(x)') ||
          formula.expression.includes('e^x')) {
        xMargin = 10000;
        yMargin = 1000000; // Very large y-margin for exponential growth
      }
      
      // For polynomial functions, especially higher degree ones, use larger margins
      if (formula.expression.includes('*x*x*x') || // Cubic or higher
          formula.expression.includes('x^3') || 
          formula.expression.includes('x**3')) {
        xMargin = 5000;
        yMargin = 100000; // Much larger y-margin for higher degree polynomials
      } else if (formula.expression.includes('*x*x') || // Quadratic
                formula.expression.includes('x^2') || 
                formula.expression.includes('x**2')) {
        xMargin = 3000;
        yMargin = 50000;
      }
      
      return x >= -xMargin && x <= canvasWidth + xMargin && 
             y >= -yMargin && y <= canvasHeight + yMargin;
    };

    // Function to detect large jumps in y values that indicate discontinuities
    const isDiscontinuity = (p1: FormulaPoint, p2: FormulaPoint): boolean => {
      if (!p1.isValid || !p2.isValid) return true;
      
      // Check for large vertical jumps that indicate discontinuities
      // Use different thresholds based on function type
      let MAX_JUMP = 100; // Default
      
      // For exponential functions, use a much higher threshold
      if (formula.expression.includes('Math.exp(x)') || 
          formula.expression.includes('exp(x)') ||
          formula.expression.includes('e^x')) {
        MAX_JUMP = 10000; // Exponential functions can have very large jumps
      }
      
      // For polynomial functions, use a higher threshold
      if (formula.expression.includes('*x*x') || 
          formula.expression.includes('x^') || 
          formula.expression.includes('x**')) {
        MAX_JUMP = 500;
      }
      
      // For tangent functions, use a much higher threshold
      if (isTangent || 
          formula.expression.includes('Math.tan(x)') || 
          formula.expression.includes('tan(x)')) {
        MAX_JUMP = 50000; // Tangent functions have vertical asymptotes
        
        // Special handling for tangent asymptotes
        // Check if the points are on opposite sides of an asymptote
        // For tan(x), asymptotes occur at x = (n + 0.5) * π
        const x1 = (p1.x - gridPosition.x) / pixelsPerUnit;
        const x2 = (p2.x - gridPosition.x) / pixelsPerUnit;
        
        // Check if there's a potential asymptote between x1 and x2
        // For tan(x), asymptotes are at x = (n + 0.5) * π
        const PI = Math.PI;
        const checkForAsymptote = (x1: number, x2: number): boolean => {
          // For tangent functions, we need to check if there's an asymptote between x1 and x2
          // Asymptotes occur at x = (n + 0.5) * π, where n is an integer
          
          // Make sure x1 < x2
          if (x1 > x2) {
            const temp = x1;
            x1 = x2;
            x2 = temp;
          }
          
          // More precise asymptote detection
          // Calculate the exact positions of asymptotes
          // Asymptotes occur at x = (n + 0.5) * π
          
          // Find the range of n values that could have asymptotes between x1 and x2
          const n1 = Math.floor(x1 / PI - 0.5);
          const n2 = Math.ceil(x2 / PI - 0.5);
          
          // Check each potential asymptote in the range
          for (let n = n1; n <= n2; n++) {
            const asymptote = (n + 0.5) * PI;
            if (asymptote > x1 && asymptote < x2) {
              return true; // Found an asymptote between the points
            }
          }
          
          // Additional check for points that are very close to asymptotes
          // This helps catch cases where numerical precision issues might miss an asymptote
          const distToAsymptote1 = Math.abs((x1 / PI - 0.5) % 1 - 0.5) * PI;
          const distToAsymptote2 = Math.abs((x2 / PI - 0.5) % 1 - 0.5) * PI;
          
          // If either point is very close to an asymptote (within 0.01 radians)
          // and they're on opposite sides, consider it a discontinuity
          if ((distToAsymptote1 < 0.01 || distToAsymptote2 < 0.01) && 
              Math.sign(Math.tan(x1)) !== Math.sign(Math.tan(x2))) {
            return true;
          }
          
          return false;
        };
        
        // If the points are likely on opposite sides of an asymptote, consider it a discontinuity
        if (checkForAsymptote(x1, x2)) {
          return true;
        }
        
        // Additional check: if the y-values have opposite signs and are both large,
        // it's likely an asymptote crossing
        const y1 = (gridPosition.y - p1.y) / pixelsPerUnit; // Convert to math coordinates
        const y2 = (gridPosition.y - p2.y) / pixelsPerUnit;
        
        // More robust check for asymptote crossing based on y-values
        // If both y-values are large in magnitude but have opposite signs,
        // and the x-values are close, it's very likely an asymptote
        if (Math.abs(y1) > 5 && Math.abs(y2) > 5 && 
            Math.sign(y1) !== Math.sign(y2) && 
            Math.abs(x2 - x1) < PI / 4) {
          return true;
        }
        
        // Another check: if the slope between points is extremely steep,
        // it's likely near an asymptote
        const slope = Math.abs((y2 - y1) / (x2 - x1));
        if (slope > 100) {
          return true;
        }
      }
      
      // Calculate the vertical distance between points
      const dy = Math.abs(p2.y - p1.y);
      
      // Calculate the horizontal distance between points
      const dx = Math.abs(p2.x - p1.x);
      
      // If the points are very close horizontally but far apart vertically,
      // it's likely a discontinuity
      return dy > MAX_JUMP && dx < 50;
    };

    // First pass: process points and handle discontinuities
    let i = 0;
    
    // Special handling for tangent functions to ensure proper asymptote rendering
    if (isTangent || 
        formula.expression.includes('Math.tan(x)') || 
        formula.expression.includes('tan(x)')) {
      
      // For tangent functions, we need to be more careful about discontinuities
      // We'll process points in segments between asymptotes
      
      while (i < points.length) {
        const point = points[i];
        const withinCanvas = isWithinCanvas(point.x, point.y);
        
        if (point.isValid && withinCanvas) {
          // Check for discontinuity with previous point
          if (i > 0 && isDiscontinuity(points[i-1], point)) {
            // End the current path segment at asymptote
            penDown = false;
          }
          
          if (!penDown) {
            // Start a new subpath
            path += ` M ${point.x},${point.y}`;
            penDown = true;
          } else {
            // Continue the path
            path += ` L ${point.x},${point.y}`;
          }
        } else {
          // Invalid point or outside canvas, lift the pen
          penDown = false;
        }
        
        i++;
      }
    } else {
      // Standard handling for non-tangent functions
      while (i < points.length) {
        const point = points[i];
        const withinCanvas = isWithinCanvas(point.x, point.y);
        
        if (point.isValid && withinCanvas) {
          // Check for discontinuity with previous point
          if (i > 0 && isDiscontinuity(points[i-1], point)) {
            // End the current path segment
            penDown = false;
          }
          
          if (!penDown) {
            // Start a new subpath
            path += ` M ${point.x},${point.y}`;
            penDown = true;
          } else {
            // Continue the path
            path += ` L ${point.x},${point.y}`;
          }
        } else {
          // Invalid point or outside canvas, lift the pen
          penDown = false;
        }
        
        i++;
      }
    }

    return path;
  };

  const pathString = createPath(points);

  // Handle path click to select a point
  const handlePathClick = (event: React.MouseEvent | TouchEvent) => {
    if (!onPointSelect) return;
    
    // Don't handle point selection if we're in the middle of a grid drag operation
    if (isDraggingRef.current) {
      return;
    }
    
    // Stop propagation to prevent the canvas from handling the click
    event.stopPropagation();
    
    // Get coordinates from either mouse or touch event
    let clientX: number;
    let clientY: number;
    
    if ('touches' in event || 'changedTouches' in event) {
      // Touch event
      const touchEvent = event as TouchEvent;
      const touch = touchEvent.changedTouches?.[0] || touchEvent.touches?.[0];
      if (!touch) return;
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else {
      // Mouse event
      const mouseEvent = event as React.MouseEvent;
      clientX = mouseEvent.clientX;
      clientY = mouseEvent.clientY;
    }
    
    // Get click coordinates relative to the SVG
    const svgElement = (event.target as Element).closest('svg');
    if (!svgElement) return;
    
    const svgRect = svgElement.getBoundingClientRect();
    const clickX = clientX - svgRect.left;
    const clickY = clientY - svgRect.top;
    
    // Find the closest point to the click
    if (!points || points.length === 0) return;
    
    let closestPointIndex = -1;
    let minDistance = Number.MAX_VALUE;
    
    points.forEach((point, index) => {
      if (!point.isValid) return;
      
      const dx = point.x - clickX;
      const dy = point.y - clickY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestPointIndex = index;
      }
    });
    
    // Use larger touch area for mobile devices
    const touchRadius = isMobile ? 44 : 18;
    
    // Only select if within a reasonable distance
    if (closestPointIndex >= 0 && minDistance <= touchRadius) {
      const point = points[closestPointIndex];
      
      // Update local state
      setSelectedPoint({ index: closestPointIndex, point });
      
      // Convert canvas coordinates back to mathematical coordinates
      // The pixelsPerUnit parameter already includes the zoom factor (zoomedPixelsPerUnit)
      // passed from the GeometryCanvas component
      const rawMathX = (point.x - gridPosition.x) / pixelsPerUnit;
      const rawMathY = -(point.y - gridPosition.y) / pixelsPerUnit;

      // Snap math X to the nearest 0.1 for stable navigation across zoom levels
      const snappedMathX = Math.round(rawMathX * 10) / 10;

      // Recompute Y using the formula at the snapped X
      let snappedMathY = rawMathY;
      try {
        const fn = createFunctionFromExpression(formula.expression, formula.scaleFactor);
        const computedY = fn(snappedMathX);
        if (Number.isFinite(computedY)) {
          snappedMathY = computedY;
        }
      } catch {
        // Ignore and keep derived Y
      }

      // Convert back to canvas coordinates with snapped math values
      const snappedCanvasX = gridPosition.x + snappedMathX * pixelsPerUnit;
      const snappedCanvasY = gridPosition.y - snappedMathY * pixelsPerUnit;

      // Notify parent
      onPointSelect({
        x: snappedCanvasX,
        y: snappedCanvasY,
        isValid: true,
        mathX: snappedMathX,
        mathY: snappedMathY,
        formula,
        pointIndex: closestPointIndex,
        allPoints: points
      });
    } else {
      // If no point is close enough, clear the selection
      setSelectedPoint(null);
      onPointSelect(null);
    }
  };

  // If we're updating and there's no path, show a placeholder
  if (isUpdating && !pathString) {
    return (
      <g>
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          fill={formula.color}
          fontSize="12"
          opacity="0.7"
        >
          Updating formula...
        </text>
        <circle
          cx="50%"
          cy="55%"
          r="5"
          fill="none"
          stroke={formula.color}
          strokeWidth="2"
          opacity="0.7"
          className="animate-spin"
        />
      </g>
    );
  }

  if (!pathString) {
    return null;
  }

  // Use a key that includes the grid position to force re-rendering when grid moves
  // For tangent functions, we use a fixed key since we're always showing the same period
  const renderKey = isTangent 
    ? `${formula.id}-tangent-fixed` 
    : `${formula.id}-${gridPositionKey}`;

  return (
    <>
      {isUpdating && (
        <text x="50%" y="50%" textAnchor="middle" fill="gray">
          Updating...
        </text>
      )}
      
      {pathString && (
        <path
          key={renderKey}
          d={pathString}
          stroke={formula.color}
          strokeWidth={formula.strokeWidth}
          fill="none"
          className="formula-graph"
          style={{ 
            opacity: isUpdating ? 0.5 : 1,
            transition: 'opacity 0.2s ease-in-out',
            pointerEvents: isDraggingRef.current ? 'none' : 'stroke', // Disable pointer events during grid dragging
            cursor: isDraggingRef.current ? 'grabbing' : (isEmbedded ? 'default' : 'crosshair') // Crosshair in default view
          }}
          onMouseDown={(e) => {
            // Prevent handling mouse down if grid is being dragged
            if (isDraggingRef.current) {
              e.stopPropagation();
              e.preventDefault();
            }
          }}
          onMouseMove={(e) => {
            // Prevent handling mouse move if grid is being dragged
            if (isDraggingRef.current) {
              e.stopPropagation();
              e.preventDefault();
            }
          }}
          onMouseUp={(e) => {
            // Prevent handling mouse up if grid is being dragged
            if (isDraggingRef.current) {
              e.stopPropagation();
              e.preventDefault();
              return;
            }
            handlePathClick(e);
          }}
          onTouchEnd={(e) => {
            // Prevent handling touch end if grid is being dragged
            if (isDraggingRef.current) {
              e.stopPropagation();
              e.preventDefault();
              return;
            }
            // Prevent default to avoid triggering mouse events
            e.preventDefault();
            handlePathClick(e.nativeEvent);
          }}
        />
      )}
      {pathString && (
        <path
          d={pathString}
          stroke="transparent"
          strokeWidth={isMobile ? 48 : 24}
          fill="none"
          className="formula-graph-hit"
          style={{ 
            pointerEvents: isDraggingRef.current ? 'none' : 'stroke', 
            vectorEffect: 'non-scaling-stroke' as any,
            cursor: isDraggingRef.current ? 'grabbing' : (isEmbedded ? 'default' : 'crosshair')
          }}
          onMouseUp={(e) => { if (!isDraggingRef.current) handlePathClick(e); }}
          onTouchEnd={(e) => { if (!isDraggingRef.current) { e.preventDefault(); handlePathClick(e.nativeEvent); } }}
        />
      )}
      
      {/* Only render the selected point if this formula has the selected point and it's not hidden */}
      {globalSelectedPoint && globalSelectedPoint.formula.id === formula.id && !hideSelectedPoint && (
        <>
          {/** Compute current canvas coordinates from math coordinates so the point stays attached across zoom/resize */}
          {(() => {
            const selectedCanvasX = gridPosition.x + (globalSelectedPoint.mathX * pixelsPerUnit);
            // Recompute Y using the formula with scaleFactor to ensure perfect adherence to the curve
            let computedY = globalSelectedPoint.mathY;
            try {
              const evaluable = buildEvaluableExpression(formula.expression, formula.variables, 'x');
              const fn = createFunctionFromExpression(evaluable, formula.scaleFactor);
              const yVal = fn(globalSelectedPoint.mathX);
              if (Number.isFinite(yVal)) {
                computedY = yVal;
              }
            } catch { /* noop */ }
            const selectedCanvasY = gridPosition.y - (computedY * pixelsPerUnit);
            const touchRadius = isMobile ? 44 : 18;
            const visualRadius = isMobile ? 8 : 5;
            const strokeWidth = isMobile ? 3 : 2;
            return (
              <g>
                {/* Invisible larger touch area for mobile */}
                {isMobile && (
                  <circle
                    cx={selectedCanvasX}
                    cy={selectedCanvasY}
                    r={touchRadius}
                    fill="transparent"
                    className="formula-point-touch-area"
                    style={{ 
                      pointerEvents: 'auto',
                      cursor: 'pointer'
                    }}
                    onMouseUp={handlePathClick}
                    onTouchEnd={(e) => {
                      // Prevent default to avoid triggering mouse events
                      e.preventDefault();
                      e.stopPropagation();
                      // Handle touch event directly
                      handlePathClick(e.nativeEvent);
                    }}
                  />
                )}
                {/* Visual point */}
                <circle
                  cx={selectedCanvasX}
                  cy={selectedCanvasY}
                  r={visualRadius}
                  fill={formula.color}
                  stroke="white"
                  strokeWidth={strokeWidth}
                  className="formula-point-selected"
                  style={{ pointerEvents: 'none' }}
                />
              </g>
            );
          })()}
        </>
      )}
    </>
  );
};

export default FormulaGraph;
